import { PaymentInfoSelected, PaymentMethodsProps } from '@msc-b2c/interfaces/types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  generatePaymentOptions,
  getAllowedCardPaymentButtons,
  getAllowededCardNetworksForSecureFields,
  getCountryCode,
  getCreditCards,
  getCurrencyCode,
  getOtherPaymentMethods,
  getPaymentButtons,
  getPaymentMethodAriaLabel,
  isApplePayEnabled,
  isGooglePayEnabled,
  isPayPalEnabled,
  MY_MSC_PAYMENT_GROUP_TO_METHOD_ID,
  RootState,
  selectedPaymentInfo,
  useScript,
} from '@msc-b2c/shared-utils';
import { useDispatch, useSelector } from 'react-redux';
import styles from './paymentsection/paymentsection.module.scss';
import { PaymentItem } from './PaymentItem';
import { Text } from '../text/Text';
import { SkeletonLoader } from '../skeleton/Skeleton';
import PayPalButton from './PaypalButton';
import PaymentButton from './PaymentButton';
import SecureFields from './SecureFields';

export function PaymentMethods({
  datatransSecureFieldUrl,
  datatransPaymentButtonUrl,
  datatransPayPalUrl,
  merchantIdBooking,
  campaignIdPaymentNotification,
  gpayMerchantId,
  cardLabel,
  cvvLabel,
  detailsLabel,
  orderButtonLabel,
  orderButtonAriaLabel,
  type,
  mandatorylabel,
  disclaimer,
  termsLabel,
  cardImg,
  expirationLabel,
  price,
  transactionId,
}: PaymentMethodsProps) {
  const [secureFieldsState, setSecureFieldsState] = useState({
    successOTPLoading: false,
    otpError: false,
    bookingsError: '',
  });

  /* PAYMENT CONTEXT AWARE */
  const allowedCards = getCreditCards();
  const allowedButtons = getPaymentButtons();
  const allowedOther = getOtherPaymentMethods();

  // create array with all card imagePath
  const cardImages: string[] = [];
  const paymentButtonsImages: string[] = [];
  const otherPaymentMethodsImages: string[] = [];
  // push all card images to the array
  allowedCards.forEach((card) => {
    if (card.pathImage) {
      cardImages.push(card.pathImage);
    }
  });

  allowedButtons.forEach((button) => {
    if (button.pathImage) {
      paymentButtonsImages.push(button.pathImage);
    }
  });

  allowedOther.forEach((other) => {
    if (other.pathImage) {
      otherPaymentMethodsImages.push(other.pathImage);
    }
  });

  const secureFieldStyles = {
    '*::placeholder': 'color: #000033',
    '*:-ms-input-placeholder': 'color: #000033',
    '*': 'color: #000033',
  };

  const [dataTransScriptLoaded, setDataTransScriptLoaded] = useState<boolean>(false);
  const { is3dsFlow } = useSelector((state: RootState) => state.payment);

  /**
   * Callback function to handle the loading of the SecureFields script.
   * If the SecureFields object is available on the window, it sets the dataTransScriptLoaded state to true.
   * Otherwise, it sets an interval to check for the SecureFields object every 200 milliseconds.
   * Once the SecureFields object is found, it sets the dataTransScriptLoaded state to true and clears the interval.
   */
  const datatransCallback = useCallback(() => {
    if (window.SecureFields) {
      setDataTransScriptLoaded(true);
    } else {
      const intervalId = setInterval(() => {
        if (window.SecureFields) {
          setDataTransScriptLoaded(true);
          clearInterval(intervalId);
        }
      }, 200);
    }
  }, []);

  const [isLoaded, hasError] = useScript({
    src: datatransSecureFieldUrl,
    async: false,
    defer: false,
    callback: datatransCallback,
  });

  const dispatch = useDispatch();
  const selectedPayment = useSelector((state: RootState) => state.payment.selectedPayment);
  const paymentMethodAriaLabels = useSelector(
    (state: RootState) => state.genericConfigs.configs.bookingFunnelConfig.paymentMethodAriaLabels
  );
  const paymentMethodAccessibilityLabels = useSelector(
    (state: RootState) =>
      state.genericConfigs.configs.bookingFunnelConfig.paymentMethodAccessibilityLabels
  );

  const resolvePaymentMethodAriaLabel = useCallback(
    (methodId: string) =>
      getPaymentMethodAriaLabel(
        { paymentMethodAriaLabels, paymentMethodAccessibilityLabels },
        methodId
      ),
    [paymentMethodAriaLabels, paymentMethodAccessibilityLabels]
  );

  const currency = getCurrencyCode();
  const countryCode = getCountryCode() ?? '';
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [secureFields, setSecureFields] = useState<any>(null);

  useEffect(() => {
    if (isLoaded && !hasError && window.SecureFields) {
      const secureFields = new window.SecureFields();
      setSecureFields(secureFields);
      setIsLoading(false);
    }
  }, [isLoaded, hasError, dataTransScriptLoaded]);

  const handlePaymentTypeClick = (paymentMethod: string, price: string, type: string) => {
    const paymentInfo: PaymentInfoSelected = {
      type,
      price,
      paymentMethod,
    };
    dispatch(selectedPaymentInfo(paymentInfo));
  };

  const updateSecureFieldsState = (field: string, value: boolean | string) => {
    setSecureFieldsState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleSecureFields = (
    type: 'otpLoading' | 'otpError' | 'datatransError',
    value: boolean | string
  ) => {
    switch (type) {
      case 'otpLoading':
        updateSecureFieldsState('successOTPLoading', value);
        break;

      case 'otpError':
        updateSecureFieldsState('otpError', value);
        break;

      case 'datatransError':
        updateSecureFieldsState('bookingsError', value);
        break;
      default:
        throw new Error(`Unknown type: ${type}`);
    }
  };

  return !secureFieldsState.successOTPLoading ? (
    <div className={`${styles['cmp-paymentCard']}`}>
      <div className={styles['cmp-paymentCard__toggleSection']}>
        {allowedCards && allowedCards.length > 0 && (
          <PaymentItem
            type={type}
            key={`payment-item-${type}-card`}
            id={`payment-item-${type}-card`}
            images={cardImages}
            checked={selectedPayment?.paymentMethod === 'card' || !selectedPayment}
            onTypeClick={handlePaymentTypeClick}
            price={price}
            paymentMethod={'card'}
            ariaLabel={resolvePaymentMethodAriaLabel(MY_MSC_PAYMENT_GROUP_TO_METHOD_ID.card)}
          />
        )}
        {allowedButtons && allowedButtons.length > 0 && (
          <PaymentItem
            type={type}
            key={`payment-item-${type}-button`}
            id={`payment-item-${type}-button`}
            images={paymentButtonsImages}
            checked={selectedPayment?.paymentMethod === 'button'}
            onTypeClick={handlePaymentTypeClick}
            price={price}
            paymentMethod={'button'}
            ariaLabel={resolvePaymentMethodAriaLabel(MY_MSC_PAYMENT_GROUP_TO_METHOD_ID.button)}
          />
        )}
        {allowedOther && allowedOther.length > 0 && (
          <PaymentItem
            type={type}
            key={`payment-item-${type}-other`}
            id={`payment-item-${type}-other`}
            images={otherPaymentMethodsImages}
            checked={selectedPayment?.paymentMethod === 'other'}
            onTypeClick={handlePaymentTypeClick}
            price={price}
            paymentMethod={'other'}
            ariaLabel={resolvePaymentMethodAriaLabel(MY_MSC_PAYMENT_GROUP_TO_METHOD_ID.other)}
          />
        )}
      </div>

      {!isLoading || is3dsFlow || transactionId ? (
        <div className={styles['cmp-paymentCard__formSection']}>
          {((price && allowedCards.length > 0) || is3dsFlow || transactionId) && (
            <div
              className={`${
                selectedPayment?.paymentMethod === 'card' || !selectedPayment
                  ? 'cmp-paymentCard__formSection-opened'
                  : 'hide'
              }`}
            >
              <Text
                tagHtml={'span'}
                text={detailsLabel}
                styleOption={'body-normal spacer-mt-032'}
              />

              <SecureFields
                paymentType={type}
                secureFields={secureFields}
                config={{
                  paymentType: type,
                  campaignId: campaignIdPaymentNotification,
                  merchantID: merchantIdBooking,
                  totalAmount: price,
                  transactionId: transactionId, //Pass transaction Id retrieved from priceToBook api
                  fields: {
                    cardNumber: {
                      placeholderElementId: `card-number-${type}`,
                      inputType: 'text',
                      placeholder: cardLabel,
                      ariaLabel: cardLabel,
                      iframeTitle: cardLabel,
                    },
                    cvv: {
                      placeholderElementId: `cvv-number-${type}`,
                      inputType: 'text',
                      placeholder: cvvLabel,
                      ariaLabel: cvvLabel,
                      iframeTitle: cvvLabel,
                    },
                  },
                  labels: {
                    cardLabel,
                    cvvLabel,
                    orderButtonLabel,
                    orderButtonAriaLabel,
                    mandatorylabel,
                    disclaimer,
                    termsLabel,
                    expirationLabel,
                  },
                  options: {
                    paymentMethods: getAllowededCardNetworksForSecureFields(),
                    styles: secureFieldStyles,
                  },
                  cardImg,
                }}
                onSecureFieldsError={handleSecureFields}
              />
            </div>
          )}

          {transactionId &&
            price &&
            allowedOther.length > 0 &&
            isPayPalEnabled() &&
            selectedPayment?.type === type && (
              <div
                className={`${
                  selectedPayment?.paymentMethod === 'other' && selectedPayment?.type === type
                    ? 'cmp-paymentCard__formSection-opened'
                    : 'hide'
                }`}
              >
                <PayPalButton
                  datatransPayPalUrl={datatransPayPalUrl}
                  paymentType={type}
                  merchantIdBooking={merchantIdBooking}
                  termsLabel={termsLabel}
                  transactionId={transactionId}
                  amount={price}
                />
              </div>
            )}

          {price && transactionId && allowedButtons.length > 0 && (
            <div
              className={`${
                selectedPayment?.paymentMethod === 'button' && selectedPayment?.type === type
                  ? 'cmp-paymentCard__formSection-opened'
                  : 'hide'
              }`}
            >
              <PaymentButton
                datatransPaymentButtonUrl={datatransPaymentButtonUrl}
                paymentType={type}
                termsLabel={termsLabel}
                gpayMerchantId={gpayMerchantId}
                {...generatePaymentOptions(
                  price,
                  getAllowedCardPaymentButtons(),
                  isGooglePayEnabled(),
                  isApplePayEnabled(),
                  currency,
                  countryCode,
                  transactionId,
                  merchantIdBooking
                )}
              />
            </div>
          )}
        </div>
      ) : (
        <SkeletonLoader
          height={'200px'}
          styleOption="cmp-skeleton__loader"
        />
      )}
    </div>
  ) : (
    <SkeletonLoader
      height={'200px'}
      styleOption="cmp-skeleton__loader"
    />
  );
}
